/**
 * Spectrum AI — Client Library
 * Provides API functions for Reviews and Wishlist features via app proxy.
 * Loaded via: {{ 'spectrum-lib.js' | asset_url | script_tag }}
 * Namespace: window.__spectrumAi.lib
 */
(function () {
  "use strict";

  // ─── Guard ────────────────────────────────────────────────────────────────────
  if (!window.__spectrumAi?.baseProxyUrl) return;

  const baseProxyUrl = window.__spectrumAi.baseProxyUrl.replace(/\/+$/, "");
  const customer = window.__spectrumAi.customer;
  const isLoggedIn = !!customer;

  // ─── Per-Feature Base Paths ───────────────────────────────────────────────────
  const REVIEWS_BASE = `${baseProxyUrl}/reviews`;
  const WISHLIST_BASE = `${baseProxyUrl}/wishlist`;
  const GUEST_STORAGE_KEY = "__spectrum_wishlist_guest";

  // ─── Private Helpers ──────────────────────────────────────────────────────────

  const isNil = (x) => x === undefined || x === null;

  const normalizeSuccess = (data) => ({ ok: true, data });

  const normalizeError = (raw, fallbackCode) => {
    let message = "Unknown error";
    let code = fallbackCode || "UNKNOWN";

    if (raw && typeof raw === "object") {
      if (typeof raw.error === "string") message = raw.error;
      else if (typeof raw.message === "string") message = raw.message;
      if (typeof raw.code === "string") code = raw.code;
    } else if (typeof raw === "string") {
      message = raw;
    }

    return { ok: false, error: { message, code } };
  };

  const pickDefined = (obj) => {
    if (!obj) return {};
    const result = {};
    Object.keys(obj).forEach((k) => {
      if (!isNil(obj[k])) result[k] = obj[k];
    });
    return result;
  };

  const toQueryString = (params) => {
    if (!params) return "";
    return Object.keys(params)
      .filter((k) => !isNil(params[k]))
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
      .join("&");
  };

  const buildUrl = (base, path, queryParams) => {
    let url = base + (path || "");
    const qs = toQueryString(queryParams);
    if (qs) url += "?" + qs;
    return url;
  };

  const getJSON = async (url) => {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const body = await response.json();
      if (!response.ok) return normalizeError(body, `HTTP_${response.status}`);
      return normalizeSuccess(body);
    } catch (err) {
      return normalizeError(
        { error: err.message, code: "NETWORK_ERROR" },
        "NETWORK_ERROR",
      );
    }
  };

  const postJSON = async (url, body) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) return normalizeError(data, `HTTP_${response.status}`);
      return normalizeSuccess(data);
    } catch (err) {
      return normalizeError(
        { error: err.message, code: "NETWORK_ERROR" },
        "NETWORK_ERROR",
      );
    }
  };

  const postFormData = async (url, formData) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) return normalizeError(data, `HTTP_${response.status}`);
      return normalizeSuccess(data);
    } catch (err) {
      return normalizeError(
        { error: err.message, code: "NETWORK_ERROR" },
        "NETWORK_ERROR",
      );
    }
  };

  // ─── Guest Wishlist (Private) ───────────────────────────────────────────────

  const getGuestItems = () => {
    try {
      const raw = localStorage.getItem(GUEST_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed.items) ? parsed.items : [];
    } catch (e) {
      return [];
    }
  };

  const addGuestItem = (item) => {
    try {
      const items = getGuestItems();
      const isDuplicate = items.some(
        (existing) =>
          existing.shopifyProductGid === item.shopifyProductGid &&
          (existing.shopifyVariantGid || null) ===
            (item.shopifyVariantGid || null),
      );
      if (isDuplicate) return;
      const entry = Object.assign({}, item);
      if (!entry.addedAt) entry.addedAt = new Date().toISOString();
      items.push(entry);
      localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify({ items }));
    } catch (e) {
      // localStorage unavailable — silently fail
    }
  };

  const removeGuestItem = (productGid) => {
    try {
      const items = getGuestItems();
      const filtered = items.filter(
        (item) => item.shopifyProductGid !== productGid,
      );
      localStorage.setItem(
        GUEST_STORAGE_KEY,
        JSON.stringify({ items: filtered }),
      );
    } catch (e) {
      // silently fail
    }
  };

  const clearGuestItems = () => {
    try {
      localStorage.removeItem(GUEST_STORAGE_KEY);
    } catch (e) {
      // silently fail
    }
  };

  const mergeGuestToServer = async (items) => {
    return postJSON(buildUrl(WISHLIST_BASE, "/merge"), { items });
  };

  // ─── Reviews API ──────────────────────────────────────────────────────────────

  const reviews = {
    getReviews: async (productHandle, opts) => {
      return getJSON(
        buildUrl(
          REVIEWS_BASE,
          `/${encodeURIComponent(productHandle)}`,
          pickDefined(opts),
        ),
      );
    },

    submitReview: async (data) => {
      return postJSON(buildUrl(REVIEWS_BASE, ""), data);
    },

    voteOnReview: async (reviewId, voteType) => {
      return postJSON(
        buildUrl(REVIEWS_BASE, `/${encodeURIComponent(reviewId)}/vote`),
        { voteType },
      );
    },

    getStats: async (productHandle) => {
      return getJSON(
        buildUrl(REVIEWS_BASE, `/stats/${encodeURIComponent(productHandle)}`),
      );
    },

    uploadMedia: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      return postFormData(buildUrl(REVIEWS_BASE, "/media/upload"), formData);
    },
  };

  // ─── Wishlist API ─────────────────────────────────────────────────────────────

  const wishlist = {
    getWishlists: async (opts) => {
      return getJSON(buildUrl(WISHLIST_BASE, "", pickDefined(opts)));
    },

    createWishlist: async (name) => {
      return postJSON(buildUrl(WISHLIST_BASE, ""), { name });
    },

    updateWishlist: async (id, data) => {
      return postJSON(
        buildUrl(WISHLIST_BASE, `/${encodeURIComponent(id)}/update`),
        data,
      );
    },

    deleteWishlist: async (id) => {
      return postJSON(
        buildUrl(WISHLIST_BASE, `/${encodeURIComponent(id)}/delete`),
        {},
      );
    },

    addItem: async (wishlistId, data) => {
      if (!isLoggedIn) {
        addGuestItem(data);
        return normalizeSuccess({ guest: true });
      }
      return postJSON(
        buildUrl(WISHLIST_BASE, `/${encodeURIComponent(wishlistId)}/items`),
        data,
      );
    },

    removeItem: async (wishlistId, itemId) => {
      return postJSON(
        buildUrl(
          WISHLIST_BASE,
          `/${encodeURIComponent(wishlistId)}/items/${encodeURIComponent(itemId)}/delete`,
        ),
        {},
      );
    },

    updateItem: async (wishlistId, itemId, data) => {
      return postJSON(
        buildUrl(
          WISHLIST_BASE,
          `/${encodeURIComponent(wishlistId)}/items/${encodeURIComponent(itemId)}/update`,
        ),
        data,
      );
    },

    removeByProduct: async (productGid) => {
      if (!isLoggedIn) {
        removeGuestItem(productGid);
        return normalizeSuccess({ guest: true, removedCount: 1 });
      }
      return postJSON(buildUrl(WISHLIST_BASE, "/items/remove-by-product"), {
        shopifyProductGid: productGid,
      });
    },

    getShared: async (shareToken, opts) => {
      return getJSON(
        buildUrl(
          WISHLIST_BASE,
          `/shared/${encodeURIComponent(shareToken)}`,
          pickDefined(opts),
        ),
      );
    },
  };

  // ─── Attach to Namespace ──────────────────────────────────────────────────────

  window.__spectrumAi.lib = {
    reviews,
    wishlist,
  };

  // ─── Auto-Merge Guest Items on Login ────────────────────────────────────────

  if (isLoggedIn) {
    const guestItems = getGuestItems();
    if (guestItems.length > 0) {
      mergeGuestToServer(guestItems).then((result) => {
        if (result.ok) clearGuestItems();
      });
    }
  }
})();
